import { type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { properties, propertyEndpoints } from './endpoints.js';
import { type CountryOption, type NamedCodeOption, type PropertyDetail } from './schemas.js';

/**
 * Reads for the Properties module.
 *
 * The list is not here: `ResourceListPage` owns list fetching so that search,
 * sort, filtering, pagination and column selection are implemented once rather
 * than per resource. A module supplies the descriptor and the schema.
 *
 * What is here is the detail read and the reference vocabularies the form
 * needs. They are queries rather than constants because they are the
 * platform's data: a currency the operator has retired, or a state added to a
 * country last week, is right in the database and stale in a bundle.
 */

const optionSchema = z.object({
  value: z.string(),
  label: z.string(),
  meta: z.record(z.string(), z.string().nullable()).optional(),
});

/**
 * The option shape, as the form's vocabulary.
 *
 * Mapped here rather than changing every consumer: the form is written in
 * `{ code, name }` throughout, and `value` is the code — the options endpoint
 * returns natural codes precisely so the write path accepts what a picker
 * offers.
 */
const countryOptions = z.array(optionSchema).transform((options) =>
  options.map((option) => ({
    code: option.value,
    name: option.label,
    phonePrefix: option.meta?.phonePrefix ?? null,
  })),
);

const codeNameOptions = z
  .array(optionSchema)
  .transform((options) => options.map((option) => ({ code: option.value, name: option.label })));

export function useProperty(propertyId: string): UseQueryResult<PropertyDetail> {
  return properties.useItem(propertyId);
}

export { useCurrencies } from '../../../lib/reference/currencies.js';

export function useCountries(q = ''): UseQueryResult<readonly CountryOption[]> {
  return useApiQuery({
    queryKey: [...queryKeys.reference.countries, q],
    path: propertyEndpoints.countries,
    query: { q },
    schema: countryOptions,
  });
}

/**
 * The states of one country.
 *
 * Disabled until a country is chosen rather than fetching them all: a state
 * code is unique within its country and meaningless without it, so a request
 * with no country would have nothing to ask for.
 */
export function useStates(countryCode: string, q = ''): UseQueryResult<readonly NamedCodeOption[]> {
  return useApiQuery({
    queryKey: [...queryKeys.reference.states(countryCode), q],
    path: propertyEndpoints.states(countryCode),
    query: { q },
    schema: codeNameOptions,
    enabled: countryCode !== '',
  });
}

export function useCities(
  countryCode: string,
  stateCode: string,
  q = '',
): UseQueryResult<readonly NamedCodeOption[]> {
  return useApiQuery({
    queryKey: [...queryKeys.reference.cities(countryCode, stateCode), q],
    path: propertyEndpoints.cities(countryCode, stateCode),
    query: { q },
    schema: codeNameOptions,
    enabled: countryCode !== '' && stateCode !== '',
  });
}
